import React from 'react';
import { isResizeObserverSupported } from '../../../common/utils/is-resize-observable-supported';
/*
 * When user fetches next or previous page we want previous comments to visually stay in same place
 * relative to viewport, so the user would not loose  previous context, but that works out of the box
 * only in scenario when user fetches page at the bottom and only when we don't do truncation from the top.
 * So this custom solution was implemented to solve the issue:
 *
 * 1. On pagination actions we set the comment id (scrollAnchorId) which should stay in the same place
 * relative to viewport after the new page is added.
 * 2. The hook useScrollAnchoring saves anchors position and restores it after render.
 */
export const useScrollAnchoring = (scrollAnchor) => {
    if (typeof document === 'undefined') {
        return;
    }
    // Get anchor elements position in render
    const scrollAnchorElement = scrollAnchor ? document.getElementById(scrollAnchor.id) : undefined;
    const originalPosition = scrollAnchorElement
        ? getCurrentPossition(scrollAnchorElement)
        : undefined;
    // eslint-disable-next-line react-hooks/rules-of-hooks
    React.useLayoutEffect(() => {
        if (scrollAnchorElement && originalPosition) {
            if (scrollAnchor?.onlyIfAboveVieport && originalPosition > 0) {
                return;
            }
            // Restore elements position after render
            scrollToOriginalPosition(scrollAnchorElement, originalPosition);
            // When ricos viewer has 'read more' button it loads it asyncronously and our position is changed after
            // adjustment in previous line. We fix that with next lines. ResizeObserver is not supported by older
            // browsers so maybe polyfill should be used, but it should be tested. And maybe we could find some
            // sollution how to work around Ricos Viewer delayed rendering issues.
            if (isResizeObserverSupported()) {
                const resizeObserver = new window.ResizeObserver(() => scrollToOriginalPosition(scrollAnchorElement, originalPosition));
                resizeObserver.observe(document.body);
                setTimeout(() => resizeObserver.disconnect(), 0);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrollAnchor?.id, scrollAnchor?.timestamp]);
};
function getCurrentPossition(element) {
    return element.getBoundingClientRect().top;
}
function scrollToOriginalPosition(element, originalPosition) {
    const adjustment = originalPosition - element.getBoundingClientRect().top;
    window.scroll(element.ownerDocument.defaultView.pageXOffset, element.ownerDocument.defaultView.pageYOffset - adjustment);
    element.focus(); // for a11y, so that focus stays where user was
}
//# sourceMappingURL=scrollAnchoring.js.map